/*
 * CHI-29: /my-events/:slug/tickets — the buyer's tickets for one event, one large QR each
 * (guests included), with a print view (one ticket per page, no nav/header/footer).
 * Data: the same single events.myTickets() call as the My Events tiles. Another member's
 * URL (no tickets of theirs for this slug) shows not-found and leaks nothing.
 */
import { useParams } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { getEventBySlug } from "@/lib/eventsData";
import { TicketQrCard } from "@/components/tickets/MyTickets";
import { useMyTicketsBySlug } from "@/hooks/useMyTicketsBySlug";
import { useServerNow } from "@/lib/serverClock";
import { UNKNOWN_EVENT_TITLE, useEventTitle } from "@/hooks/useEventTitle";

/** Scoped to this view: only rendered while it is mounted. */
export const MY_EVENT_TICKETS_PRINT_CSS = `
@media print {
  .chi-print-hide { display: none !important; }
  .chi-my-event-tickets [data-slot="edit-guest"] { display: none !important; }
  .chi-my-event-tickets { background: white !important; color: black !important; padding: 0 !important; }
  .chi-my-event-tickets * { color: black !important; }
  .chi-my-event-tickets ul { display: block !important; }
  .chi-print-ticket { break-after: page; page-break-after: always; break-inside: avoid; min-height: 90vh; justify-content: center; }
  .chi-print-ticket:last-child { break-after: auto; page-break-after: auto; }
}`;

export default function MyEventTickets() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { isLoggedIn } = useAuth();
  const mine = useMyTicketsBySlug(isLoggedIn);
  const now = useServerNow(mine.serverNow, 30_000);
  const event = getEventBySlug(slug);
  const catalogTitle = useEventTitle(slug);
  const eventName = event?.title ?? catalogTitle ?? UNKNOWN_EVENT_TITLE;
  const tickets = mine.bySlug.get(slug) ?? [];
  const notFound = !isLoggedIn || mine.status === "error" || (mine.status === "ok" && tickets.length === 0);

  return (
    <div className="chi-my-event-tickets" style={{ background: "#080C18", minHeight: "100vh" }}>
      <style>{MY_EVENT_TICKETS_PRINT_CSS}</style>
      <div className="chi-print-hide" data-testid="print-hide-nav"><Navigation /></div>
      <main style={{ maxWidth: 720, margin: "0 auto", padding: "120px 16px 64px", fontFamily: "'Inter', sans-serif", color: "#CBD5E1" }}>
        {notFound ? (
          <div role="status">
            <h1 style={{ color: "white" }}>Not found</h1>
            <p><a href="/my-events">Back to My Events</a></p>
          </div>
        ) : mine.status !== "ok" ? (
          <p role="status">Loading your tickets…</p>
        ) : (
          <>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem", color: "white" }}>{`Your tickets for ${eventName}. Show this QR at the door`}</h1>
            <div className="chi-print-hide" style={{ display: "flex", gap: "1rem", margin: "1rem 0 2rem", flexWrap: "wrap" }}>
              <button type="button" className="chi-btn-primary" onClick={() => window.print()}>Print tickets</button>
              <a href="/my-events">Back to My Events</a>
            </div>
            <ul style={{ display: "grid", gap: "2rem", padding: 0 }}>
              {tickets.map((t, index) => (
                <TicketQrCard key={t.ticketId} ticket={t} eventTitle={eventName} now={now} variant="large" details={{ date: event?.displayDate, time: event?.time, venue: event?.location }} onGuestUpdated={() => void mine.reload()} includeStyles={index === 0} />
              ))}
            </ul>
          </>
        )}
      </main>
      <div className="chi-print-hide" data-testid="print-hide-footer"><Footer /></div>
    </div>
  );
}
